import { Button, DatePicker, Icons, InputNumber, Modal, Typography, useModal } from '@pankod/refine-antd'
import { useNotification, useTranslate } from '@pankod/refine-core'
import { QueryObserverResult } from '@tanstack/react-query'
import { Dayjs } from 'dayjs'
import { User } from 'interfaces'
import { useEffect, useState } from 'react'
import { API_URL, REFERRAL_BENEFIT_STATUS_MODIFIED, SYNIVA_DEFAULT_USER, checkUserPermission, supabaseClient } from 'utility'

type ModalProps = {
  user: User
  tableQueryResult: QueryObserverResult
}

export const AddAmountToWalletModal: React.FC<ModalProps> = ({ user, tableQueryResult }) => {
  const { modalProps, show, close } = useModal()
  const { open } = useNotification()
  const [moneyToAdd, setMoneyToAdd] = useState<number>(0)
  const [walletBalance, setWalletBalance] = useState<number>(user.wallet_balance)
  const [dateLimit, setDateLimit] = useState<Dayjs | null>(null)
  const [hasPermission, setHasPermission] = useState(false)
  const t = useTranslate()

  useEffect(() => {
    const fetchPermission = async () => {
      setHasPermission(await checkUserPermission())
    }
    fetchPermission()
  }, [])

  const handleOk = async () => {
    let valid = false

    if (moneyToAdd > 0) valid = (!!user.id && moneyToAdd > 0 && dateLimit !== null)
    else valid = (!!user.id && moneyToAdd < 0)

    if (!valid) {
      open?.({
        type: 'error',
        description: t('addToWallet.emptyInputMessage'),
        message: ''
      })
      return null
    }

    const res = await fetch(`${API_URL}/users/${user.email}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        wallet_balance: walletBalance
      })
    })

    if (res.status === 200 || res.status === 204) {
      await supabaseClient
        .from('referral_benefits')
        .insert({
          referrer_user_id: SYNIVA_DEFAULT_USER,
          referred_user_id: user.id,
          benefit_amount: moneyToAdd,
          status: REFERRAL_BENEFIT_STATUS_MODIFIED,
          referred_expired_at: (moneyToAdd > 0) ? dateLimit?.toISOString() : null,
          referrer_expired_at: dateLimit?.toISOString() || new Date(Date.now()).toISOString(),
          increased_by_syniva: true
        })
      await tableQueryResult.refetch()
      open?.({
        type: 'success',
        message: t('addToWallet.successMessage', { email: user.email }),
        description: t('addToWallet.successDescription')
      })
      close()
    } else {
      open?.({
        type: 'error',
        message: t('addToWallet.errorMessage', { email: user.email }),
        description: t('addToWallet.errorDescription')
      })
    }
  }

  const showModal = () => {
    setMoneyToAdd(0)
    setWalletBalance(user.wallet_balance)
    setDateLimit(null)
    show()
  }

  const checkPermission = () => {
    return hasPermission
  }

  return (
    <>
      <Button onClick={showModal} disabled={!checkPermission()} size="small" className="ant-btn ant-btn-primary">
        <Icons.WalletOutlined />
      </Button>

      <Modal
        {...modalProps}
        title={t('addToWallet.insertMoney', { email: user.email })}
        centered
        onOk={handleOk}
        onCancel={close}
        style={{ minWidth: '40svw' }}
      >
          <Typography>{t('addToWallet.nextMoney')}</Typography>
          <InputNumber value={moneyToAdd} style={{ width: '150px', marginTop: '15px' }} onChange={(e) => {
            setMoneyToAdd(e)
            setWalletBalance((user.wallet_balance + (e) < 0) ? 0 : user.wallet_balance + (e))
          }}></InputNumber>

          <Typography>{t('addToWallet.actualMoney')}</Typography>
          <InputNumber disabled value={walletBalance} style={{ width: '150px', marginTop: '15px' }} onChange={(e) => {
            setWalletBalance(e)
          }}></InputNumber>

          <Typography>{t('addToWallet.expiredDate')}</Typography>
          <DatePicker disabled={moneyToAdd < 0} style={{ width: '150px', marginTop: '15px' }} onChange={(e) => {
            setDateLimit(e)
          }} />
      </Modal>
    </>
  )
}
